import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import { IDrService } from "@base/modules/drService/interface/drService.interface";
import { IHealthInsuranceCard } from "@base/modules/patientInsurance/healthInsurance.interface";
import { IPromotion } from "@base/modules/promotion/interface/promotion.interface";
import { ISite, ISiteBranch } from "@base/modules/site/site.interface";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { ITimeTablePeriod } from "./appointment.interface";

/** Rules of the regular (not package) booking flow, `dat-lich-thuong-web-spec.md`. */

export interface IFee {
  total_fee?: number;
  discount?: number;
  other_fee?: number;
  tax?: number;
  actual_fee?: number;
}

/**
 * Everything one booking collects, from the entry point to the "Book" button. A fresh draft per
 * booking - nothing leaks from one booking into the next.
 */
export interface BookingDraft {
  /** null: the patient still has to pick online or at a clinic. */
  online: boolean | null;
  record: IPatientRecord | null;
  site: ISite | null;
  branch: ISiteBranch | null;
  service: IDrService | null;
  /** Narrows the services to the doctor's specialty when booking from a doctor; "" otherwise. */
  specializationFilter: string;
  /** The doctor to preselect, once (booking from a doctor's profile). */
  preferredDoctorCode: string | null;
  /** A follow-up visit picks its service on the booking screen itself. */
  skipServiceStep: boolean;
  /** Set once the booking screen has been reached: later picks return straight to it. */
  reachedBooking: boolean;
  /** yyyy-MM-dd */
  date: string;
  doctor: IDoctor | null;
  slot: ITimeTablePeriod | null;
  symptoms: string;
  voucher: IPromotion | null;
  /** BHYT card - clinic visits only. */
  insurance: IHealthInsuranceCard | null;
  fee: IFee | null;
  /** Which selection `fee` was computed for - a fee for anything else is not used. */
  feeKey: string | null;
}

/** Where the draft in progress is kept for the tab, so a refresh does not lose it. */
export const BOOKING_DRAFT_KEY = "BOOKING_DRAFT";

export type BookingStep = "TYPE" | "RECORD" | "SITE" | "SERVICE" | "BOOKING" | "PAYMENT";

export const emptyDraft = (today: string): BookingDraft => ({
  online: null,
  record: null,
  site: null,
  branch: null,
  service: null,
  specializationFilter: "",
  preferredDoctorCode: null,
  skipServiceStep: false,
  reachedBooking: false,
  date: today,
  doctor: null,
  slot: null,
  symptoms: "",
  voucher: null,
  insurance: null,
  fee: null,
  feeKey: null,
});

/** The step still missing, in the order of the flow. */
export const nextStep = (draft: BookingDraft): BookingStep => {
  if (draft.online === null) {
    return "TYPE";
  }

  if (!draft.record) {
    return "RECORD";
  }

  if (!draft.site) {
    return "SITE";
  }

  if (!draft.service && !draft.skipServiceStep && !draft.reachedBooking) {
    return "SERVICE";
  }

  return "BOOKING";
};

//! What a change clears (§4)
const clearedSelection = { doctor: null, slot: null, fee: null, feeKey: null };

export const withOnline = (draft: BookingDraft, online: boolean): BookingDraft =>
  draft.online === online
    ? draft
    : { ...draft, online, service: null, voucher: null, insurance: online ? null : draft.insurance, ...clearedSelection };

export const withRecord = (draft: BookingDraft, record: IPatientRecord): BookingDraft =>
  draft.record && draft.record.id === record.id ? { ...draft, record } : { ...draft, record, service: null, voucher: null, ...clearedSelection };

export const withSite = (draft: BookingDraft, site: ISite, branch: ISiteBranch | null): BookingDraft =>
  draft.site?.code === site.code && (draft.branch?.code ?? null) === (branch?.code ?? null)
    ? { ...draft, site, branch }
    : { ...draft, site, branch, service: null, voucher: null, ...clearedSelection };

export const withService = (draft: BookingDraft, service: IDrService): BookingDraft => ({ ...draft, service, ...clearedSelection });

export const withDate = (draft: BookingDraft, date: string): BookingDraft => (draft.date === date ? draft : { ...draft, date, ...clearedSelection });

export const withDoctor = (draft: BookingDraft, doctor: IDoctor | null): BookingDraft =>
  draft.doctor?.doctor_code === doctor?.doctor_code ? { ...draft, doctor } : { ...draft, doctor, slot: null, fee: null, feeKey: null };

export const withSlot = (draft: BookingDraft, slot: ITimeTablePeriod | null): BookingDraft => ({ ...draft, slot, fee: null, feeKey: null });

export const withVoucher = (draft: BookingDraft, voucher: IPromotion | null): BookingDraft => ({ ...draft, voucher, fee: null, feeKey: null });

/** Identifies the selection a fee belongs to. */
export const selectionKey = (draft: BookingDraft) =>
  JSON.stringify([
    draft.record?.id ?? null,
    draft.online,
    draft.site?.code ?? null,
    draft.branch?.code ?? null,
    draft.service?.code ?? null,
    draft.date,
    draft.doctor?.doctor_code ?? null,
    draft.slot?.id ?? null,
    draft.voucher?.type ?? null,
  ]);

/** A fee can be asked for once a service and a slot are chosen. */
export const canCalculateFee = (draft: BookingDraft) => !!draft.service && !!draft.slot && !!draft.doctor?.doctor_code;

export const hasCurrentFee = (draft: BookingDraft) => !!draft.fee && draft.feeKey === selectionKey(draft);

/** The service belongs to the kind being booked (the app accepted either). */
export const serviceMatchesType = (service: IDrService | null, online: boolean | null) =>
  !!service && (service.online_service === undefined || service.online_service === null || service.online_service === !!online);

export const canSubmit = (draft: BookingDraft) =>
  !!draft.date &&
  !!draft.symptoms.trim() &&
  !!draft.slot &&
  !!draft.doctor?.doctor_code?.trim() &&
  serviceMatchesType(draft.service, draft.online) &&
  hasCurrentFee(draft);

/** Free or paid is the latest fee's `actual_fee`, never `is_free`. */
export const isPaid = (fee: IFee | null) => Number(fee?.actual_fee ?? 0) > 0;

/** Body shared by the fee, the clash check, the booking and the hand-over to payment (§12.1). */
export const buildAppointmentPayload = (draft: BookingDraft, options: { includeInsurance: boolean }) => {
  const service = draft.service ?? {};
  const body: Record<string, unknown> = {
    appointment_date: draft.date,
    symptoms: draft.symptoms.trim(),
    patient_record: draft.record,
    time_table_period: draft.slot,
    doctor: draft.doctor,
    doctor_code: draft.doctor?.doctor_code ?? "",
    appointment_promotions: draft.voucher ? [{ ...draft.voucher, promo_code: draft.voucher.name }] : [],
    key: "",
    is_free: false,
    appointment_used_services: [
      {
        id: Number(service.id ?? 0),
        service_id: Number(service.id ?? 0),
        code: service.code ?? "",
        name: service.name ?? "",
        description: service.description ?? "",
        note: service.note ?? "",
        amount: service.amount ?? 0,
        real_amount: service.real_amount ?? 0,
        service_type: service.service_type ?? "",
        package_type: service.package_type ?? "",
        supported_specialization: service.supported_specialization ?? "",
        patient_special_package_detail_implement_id: 0,
      },
    ],
    site_code: draft.site?.code ?? "",
    site_name: draft.site?.name ?? "",
    site_type: draft.site?.site_type ?? "",
    appointment_type: draft.online ? "ONLINE" : "OFFLINE",
    contact_phone: draft.record?.contact_phone || draft.record?.patient_phone_number || "",
  };

  if (draft.branch?.code && draft.branch?.name) {
    body.site_branch_code = draft.branch.code;
    body.site_branch_name = draft.branch.name;
  }

  if (options.includeInsurance && !draft.online && draft.insurance) {
    body.appointment_insurance = draft.insurance;
  }

  return body;
};

const ddMMyyyy = (date: string) => date.split("-").reverse().join("/");

/** The order behind a booking: 0đ for a free one, the payable amount for a paid one (§12.5). */
export const buildAppointmentOrder = (
  draft: BookingDraft,
  appointment: { key?: string; site_code?: string; site_name?: string },
  options: { bankCode: string; totalAmount: number },
) => {
  const record = draft.record ?? {};
  const body: Record<string, unknown> = {
    ref_key: appointment.key ?? "",
    order_type: "APPT",
    total_amounts: options.totalAmount,
    real_amounts: draft.fee?.total_fee ?? 0,
    discount_amounts: draft.fee?.discount ?? 0,
    customer_name: record.patient_name ?? "",
    customer_phone: record.contact_phone || record.patient_phone_number || "",
    customer_address: record.patient_address ?? "",
    customer_email: record.patient_email ?? "",
    description: `Thanh toan lich hen - ${draft.service?.note || draft.service?.name || ""} - ${ddMMyyyy(draft.date)}`,
    bank_code: options.bankCode,
    site_code: appointment.site_code || draft.site?.code || "",
    site_name: appointment.site_name || draft.site?.name || "",
    payment_url: "",
    order_time: 0,
    created_time: 0,
    payment: { vnpResponseCode: "" },
    order_key: "",
    order_trans_key: "",
    cancel: false,
    payment_method: "",
    status: "",
    refund_amounts: 0,
    deep_link: "",
  };

  if (draft.branch?.code && draft.branch?.name) {
    body.site_branch_code = draft.branch.code;
    body.site_branch_name = draft.branch.name;
  }

  return body;
};

//! Sites
const functionsOf = (site?: ISite | null) =>
  (site?.special_functions ?? "")
    .toUpperCase()
    .split(",")
    .map((value) => value.trim());

/** Only clinics offering the booking menu take regular bookings. */
export const canBookAtSite = (site?: ISite | null) => functionsOf(site).includes("SERVICE_MENU");

export const activeBranches = (site?: ISite | null) => (site?.site_branch ?? []).filter((branch) => branch.active === true);

//! Doctors
/** The doctor booked from comes first when listed; otherwise the first doctor. */
export const pickDoctor = (doctors: IDoctor[], preferredCode: string | null, currentCode?: string | null) =>
  doctors.find((doctor) => !!currentCode && doctor.doctor_code === currentCode) ??
  doctors.find((doctor) => !!preferredCode && doctor.doctor_code === preferredCode) ??
  doctors[0] ??
  null;

//! BHYT
/** A BHYT card's QR: 15 parts split by `|`, the last one `$`. */
export const isInsuranceQR = (raw: string) => {
  const parts = raw.trim().split("|");

  return parts.length === 15 && parts[14].trim() === "$";
};

//! Promotions
export type VoucherError = "NOT_EXIST" | "EXPIRED" | "ALREADY_USED" | "COMMON";

export const voucherError = (status?: number, body?: unknown): VoucherError => {
  const text = typeof body === "string" ? body : JSON.stringify(body ?? "");

  if (status === 404) {
    return "NOT_EXIST";
  }

  if (status === 423) {
    return "EXPIRED";
  }

  return text.includes("PROMOTION_APPLIED_DETECTED") ? "ALREADY_USED" : "COMMON";
};

//! Display
const WEEKDAYS: Record<string, string[]> = {
  vi: ["CN", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};

/** "Thứ 6, 9/10/2026" - "Fri, 9/10/2026" in English. */
export const formatBookingDate = (date: string, language?: string) => {
  const [y, m, d] = date.split("-").map(Number);

  if (!y || !m || !d) {
    return "";
  }

  const weekday = (WEEKDAYS[language ?? "vi"] ?? WEEKDAYS.vi)[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];

  return `${weekday}, ${d}/${m}/${y}`;
};
